import {
  Building2,
  Clock,
  MapPin,
  PenLine,
  Receipt,
  type LucideIcon,
} from "lucide-react";
import { useTranslation } from "react-i18next";

import { MarkdownView } from "@/components/shared/MarkdownView";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import type { CertificatePricingCatalogItem } from "@/services/certificatePricingCatalogService";
import {
  catalogCustomerAmountMinor,
  formatCatalogAmountMinor,
} from "@/services/certificatePricingCatalogService";
import { cn } from "@/lib/utils";
import { formatCertificatePricingProduct } from "@/utils/certificatePricingDisplay";

import { type RequestCategory } from "./new-notarial-request-types";

/*
  THE CATEGORY PICKER IS GONE, and this note is here so it is not reinvented.

  `NewRequestCategorySelection` lived here and hardcoded ["accountant",
  "notary"] as the consumer's discipline choices. It was dead: nothing imported
  it, and the only import of this module takes `NewRequestTypeSelection` below.
  Backlog task 6724dac9 and the cd4822e retrospective both recorded it as the
  reason a third profession was invisible to consumers, which made it a false
  lead twice over.

  A CONSUMER NEVER PICKS A DISCIPLINE. The category is DERIVED from the chosen
  product — useNewCertificateRequestFlow's handleCertificateTypeChange sets it
  from the request type, and certificateRequestFlow's
  categoryForCatalogProductType reads it off the catalog row's involvesNotary /
  involvesAccountant. The wizard offers products.

  And a new profession could never have appeared here anyway: it is rfq_only,
  so it has no catalog products by construction. Its consumer path is the RFQ
  intake, gated on the tramite_knowledge corpus (spec 8a948f97, RES-1).
*/

const REQUEST_TYPE_ICONS: Partial<Record<string, LucideIcon>> = {
  income_certificate: Receipt,
  notarial_legal_entity: Building2,
  notarial_signature: PenLine,
  notarial_address: MapPin,
};

export function NewRequestTypeSelection({
  allowedTypes,
  value,
  onValueChange,
  catalogItems,
  showPricing = true,
}: {
  allowedTypes: string[];
  value: string | null;
  onValueChange: (value: string) => void;
  /** Reserved for future category-scoped layouts; matching uses `targetType` only. */
  category?: RequestCategory;
  catalogItems: CertificatePricingCatalogItem[] | undefined;
  /** When false, hides catalog price lines and catalog markdown descriptions; uses short i18n hints instead. */
  showPricing?: boolean;
}): JSX.Element {
  const { t, i18n } = useTranslation();

  const resolveItem = (type: string): CertificatePricingCatalogItem | undefined => {
    if (!catalogItems?.length) return undefined;
    return catalogItems.find((c) => c.targetType === type);
  };

  return (
    <div className="space-y-4">
      <RadioGroup value={value ?? ""} onValueChange={onValueChange} className="grid gap-3">
        {allowedTypes.map((type) => {
          const TypeIcon = REQUEST_TYPE_ICONS[type] ?? Receipt;
          const selected = value === type;
          const item = resolveItem(type);
          const chargeMinor = item != null ? catalogCustomerAmountMinor(item) : null;
          const isPerFileIncome =
            type === "income_certificate" &&
            item != null &&
            item.pricingModel === "per_file" &&
            item.perFileStorageAmountMinor > 0;
          const priceLine = !showPricing
            ? ""
            : isPerFileIncome
              ? t("requests.user.requestTypePrices.income_certificate_estimate", {
                  service:
                    item != null
                      ? formatCatalogAmountMinor(
                          item.serviceFinalAmountMinor ?? item.finalAmountMinor ?? 0,
                          item.currency,
                          i18n.language
                        )
                      : "",
                  perFile:
                    item != null
                      ? formatCatalogAmountMinor(
                          item.perFileStorageAmountMinor,
                          item.currency,
                          i18n.language
                        )
                      : "",
                })
              : chargeMinor != null
                ? formatCatalogAmountMinor(chargeMinor, item!.currency, i18n.language)
                : t(`requests.user.requestTypePrices.${type}`, {
                    defaultValue: t("requests.user.requestTypePrices.fallback"),
                  });
          const hasMd = Boolean(item?.description?.trim());
          const showCatalogDescription = showPricing && hasMd;
          return (
            <div key={type}>
              <RadioGroupItem value={type} id={`nr-type-${type}`} className="peer sr-only" />
              <Label
                htmlFor={`nr-type-${type}`}
                className={cn(
                  "wizard-selectable-card flex cursor-pointer items-start gap-3 border-2 p-4 transition-colors duration-200",
                  "peer-focus-visible:outline-none peer-focus-visible:ring-2 peer-focus-visible:ring-ring peer-focus-visible:ring-offset-2",
                  selected
                    ? "border-primary bg-primary/[0.06] dark:border-primary dark:bg-primary/10"
                    : "border-border hover:border-primary dark:border-border/80 dark:bg-secondary/35 dark:shadow-md dark:shadow-black/20 dark:hover:border-primary",
                )}
              >
                <span
                  className={cn(
                    "wizard-selectable-card-icon mt-0.5 border-2 transition-colors duration-200",
                    selected
                      ? "border-primary text-primary dark:border-primary"
                      : "border-border bg-background dark:border-border/80 dark:bg-secondary/55",
                  )}
                  aria-hidden
                >
                  <TypeIcon className="h-5 w-5" strokeWidth={1.75} />
                </span>
                <span className="min-w-0 flex-1 space-y-1.5">
                  <span className="block text-base font-semibold leading-snug text-foreground">
                    {formatCertificatePricingProduct(type, t)}
                  </span>
                  {showCatalogDescription ? (
                    <MarkdownView markdown={item!.description!.trim()} />
                  ) : (
                    <span
                      className={cn(
                        "block text-sm leading-relaxed",
                        selected ? "text-muted-foreground" : "text-foreground/80",
                      )}
                    >
                      {t(`requests.user.requestTypeHints.${type}`, {
                        defaultValue: t("requests.user.requestTypeHints.fallback"),
                      })}
                    </span>
                  )}
                  {priceLine !== "" ? (
                    <span className="block text-base font-bold tracking-tight text-primary">{priceLine}</span>
                  ) : null}
                  {item != null && item.defaultBusinessDays > 0 ? (
                    <span className="mt-1 inline-flex items-center gap-1 rounded-full border border-border bg-muted/60 px-2.5 py-0.5 text-xs font-medium text-muted-foreground">
                      <Clock className="h-3 w-3" />
                      {t("requests.user.estimatedBusinessDays", { days: item.defaultBusinessDays })}
                    </span>
                  ) : null}
                </span>
              </Label>
            </div>
          );
        })}
      </RadioGroup>
    </div>
  );
}
